// Public header bell (Phase 46, 46-20). UI-SPEC contract 1, D-11..D-13.
//
// Header mounts two instances: placement="desktop" inside the md+ auth cluster and
// placement="mobile" in the bar. Each renders only when the viewport matches its placement, so
// exactly one bell is visible; they share one TanStack query, so polling is not doubled.
//
// SSR: the server renders `user: null`, and the embedded-preview check runs in an effect, so the
// bell never renders on the server and never inside the moderator preview iframe (T-46-20-03).
//
// Top slot (46-24): at most one prompt card. The D-16 WhatsApp card wins while the server says it
// is eligible; otherwise, on the user's first-ever bell open (BELL_FIRST_OPEN_KEY, shared with the
// /notifications page so desktop dropdown and mobile inbox count as one "first open"), the D-17
// push soft ask shows with surface bell_first_open. Signed-in users whose browser already granted
// push re-sync their existing subscription once per signed-in user (no prompt, no registration).

import { useEffect, useRef, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell } from "lucide-react";
import { toast } from "sonner";
import { NOTIFICATION_REGISTRY, notificationTypeMap, type NotificationType } from "@shared/notifications";
import { NOTIFICATION_CHROME } from "@shared/notificationCopy";
import { useAuth } from "@/contexts/AuthContext";
import { useLocation } from "@/hooks/useLocation";
import { usePostHogTracking } from "@/hooks/usePostHogTracking";
import { isEmbeddedPreview } from "@/lib/embeddedPreview";
import { Link } from "@/components/Link";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  getNotifications,
  getUnreadCount,
  markAllNotificationsRead,
  markNotificationsSeen,
} from "@/api/notificationService";
import {
  NOTIFICATION_QUERY_KEYS,
  POLL_INTERVAL_MS,
  UNREAD_STALE_MS,
  formatBadgeCount,
} from "@/lib/notificationUi";
import { syncExistingPushSubscription } from "@/lib/pushSubscription";
import { shouldSuppressBellWhatsappCard } from "@/lib/onboardingUi";
import NotificationList from "./NotificationList";
import PushSoftAsk from "./PushSoftAsk";
import WhatsappOptInCard, { fetchNotificationPreferences, isWhatsappPromptVisible } from "./WhatsappOptInCard";

const DESKTOP_QUERY = "(min-width: 768px)";
const DROPDOWN_LIMIT = 10;
const LANDING_SOURCES = ["push", "email", "whatsapp"] as const;
type LandingSource = (typeof LANDING_SOURCES)[number];

export const UNREAD_COUNT_KEY = NOTIFICATION_QUERY_KEYS.unreadCount("public");
const DROPDOWN_LIST_KEY = [...NOTIFICATION_QUERY_KEYS.list("public"), "latest"] as const;

/** Unseen-count query shared by the bell and the inbox page. Data shape: `{ count }`. */
export const fetchPublicUnreadCount = async (): Promise<{ count: number }> => {
  const res = await getUnreadCount({ audience: "public" });
  if (!res.ok) throw new Error(res.error);
  return { count: res.data?.count ?? 0 };
};

type Props = {
  placement: "desktop" | "mobile";
  /** Rendered above the dropdown header. 46-24 fills it (WhatsApp opt-in card or push soft ask). */
  topSlot?: ReactNode;
};

// Guard for the push/email landing capture: two bell instances mount per page.
let landingCapturedFor: string | null = null;

// Two bell instances mount per page; re-sync the push subscription once per signed-in user, so an
// account switch in the same tab (no reload) re-binds the endpoint to the new user.
let pushSyncedForUser: string | null = null;

/** D-17 "first bell open" marker, shared by the desktop dropdown and the /notifications page. */
export const BELL_FIRST_OPEN_KEY = "yf_bell_opened_v1";

/**
 * Returns true exactly once per browser (the first call), then false. Writes the marker as it
 * answers. Browser-only: callers invoke it from effects or handlers, never during render.
 */
export const claimBellFirstOpen = (): boolean => {
  if (typeof window === "undefined") return false;
  try {
    if (window.localStorage.getItem(BELL_FIRST_OPEN_KEY)) return false;
    window.localStorage.setItem(BELL_FIRST_OPEN_KEY, new Date().toISOString());
    return true;
  } catch {
    // Storage blocked (private mode): never nag on every open.
    return false;
  }
};

export default function NotificationBell({ placement, topSlot }: Props) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  const { trackNotifications } = usePostHogTracking();
  const [embedded, setEmbedded] = useState(true);
  const [isDesktop, setIsDesktop] = useState<boolean | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setEmbedded(isEmbeddedPreview());
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      setIsDesktop(false);
      return undefined;
    }
    const mql = window.matchMedia(DESKTOP_QUERY);
    const update = () => setIsDesktop(mql.matches);
    update();
    mql.addEventListener("change", update);
    return () => mql.removeEventListener("change", update);
  }, []);

  const onModerator = pathname?.startsWith("/moderator") ?? false;
  const active = !!user && !embedded && !onModerator;

  const { data: unread } = useQuery({
    queryKey: UNREAD_COUNT_KEY,
    queryFn: fetchPublicUnreadCount,
    enabled: !!user && !embedded,
    refetchInterval: POLL_INTERVAL_MS,
    refetchOnWindowFocus: "always",
    staleTime: UNREAD_STALE_MS,
  });

  const listQuery = useQuery({
    queryKey: DROPDOWN_LIST_KEY,
    queryFn: async () => {
      const res = await getNotifications({ audience: "public", limit: DROPDOWN_LIMIT });
      if (!res.ok) throw new Error(res.error);
      return res.data?.items ?? [];
    },
    enabled: active && open,
    // Always stale: the global 5-minute staleTime would otherwise reopen the bell on a cached
    // list that lacks the item the badge just announced, and opening marks it all seen.
    staleTime: 0,
    refetchOnMount: "always",
  });

  // Same cache entry as the cards and the preferences matrix (unwrapped preferences).
  const prefsQuery = useQuery({
    queryKey: NOTIFICATION_QUERY_KEYS.preferences,
    queryFn: fetchNotificationPreferences,
    enabled: active && placement === "desktop",
    retry: false,
  });
  // D-23 (49-15): a finish-screen decliner under 8 days old gets their one second ask elsewhere
  // (thank-you or welcome card), so the bell/inbox card stays hidden while the server flag is on.
  const whatsappVisible =
    isWhatsappPromptVisible(prefsQuery.data) && !shouldSuppressBellWhatsappCard({ prefs: prefsQuery.data });
  const prefsSettled = !!prefsQuery.data || prefsQuery.isError;
  const whatsappShown = useRef(false);
  const pushDecided = useRef(false);
  const [showPushAsk, setShowPushAsk] = useState(false);

  useEffect(() => {
    if (open && whatsappVisible) whatsappShown.current = true;
  }, [open, whatsappVisible]);

  // First-ever open with no WhatsApp card on screen: the push soft ask gets its one chance. The
  // marker is only consumed here, so a first open taken by the WhatsApp card leaves it for later.
  useEffect(() => {
    if (!open || !prefsSettled || whatsappVisible || whatsappShown.current || pushDecided.current) return;
    pushDecided.current = true;
    setShowPushAsk(claimBellFirstOpen());
  }, [open, prefsSettled, whatsappVisible]);

  const userId = user?._id ?? null;
  useEffect(() => {
    if (!active || !userId || pushSyncedForUser === userId) return;
    pushSyncedForUser = userId;
    void syncExistingPushSubscription();
  }, [active, userId]);

  // Push/email/WhatsApp landing: the deep link carries nsrc + ntype (withNotificationSource). Capture once,
  // then strip only those two params so focusReview etc. stay intact.
  useEffect(() => {
    if (!active || placement !== "desktop" || typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const nsrc = params.get("nsrc");
    const ntype = params.get("ntype");
    if (!nsrc && !ntype) return;
    const href = window.location.href;
    if (landingCapturedFor === href) return;
    landingCapturedFor = href;
    const validSource = (LANDING_SOURCES as readonly string[]).includes(nsrc ?? "");
    const validType =
      !!ntype &&
      Object.prototype.hasOwnProperty.call(notificationTypeMap, ntype) &&
      NOTIFICATION_REGISTRY[ntype as NotificationType].audience === "public";
    if (validSource && validType) {
      trackNotifications.clicked({ type: ntype as NotificationType, surface: nsrc as LandingSource });
    }
    params.delete("nsrc");
    params.delete("ntype");
    const qs = params.toString();
    window.history.replaceState(window.history.state, "", `${window.location.pathname}${qs ? `?${qs}` : ""}${window.location.hash}`);
  }, [active, placement, trackNotifications]);

  const handleOpenChange = async (next: boolean) => {
    setOpen(next);
    if (!next) return;
    queryClient.setQueryData(UNREAD_COUNT_KEY, { count: 0 });
    await markNotificationsSeen({ audience: "public" });
  };

  const handleMarkAll = async () => {
    const res = await markAllNotificationsRead({ audience: "public" });
    if (!res.ok) {
      toast.error(NOTIFICATION_CHROME.markAllFailedToast);
      return;
    }
    await queryClient.invalidateQueries({ queryKey: NOTIFICATION_QUERY_KEYS.list("public") });
  };

  if (!active || isDesktop === null) return null;
  if (placement === "desktop" && !isDesktop) return null;
  if (placement === "mobile" && isDesktop) return null;

  const count = unread?.count ?? 0;
  const badge = formatBadgeCount(count);
  const ariaLabel = count > 0 ? NOTIFICATION_CHROME.bellAriaWithCount(count) : NOTIFICATION_CHROME.bellAria;
  const size = placement === "desktop" ? 36 : 44;

  const hitArea = {
    position: "relative" as const,
    width: size,
    height: size,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: "none",
    background: "transparent",
    color: "var(--ys-ink)",
    borderRadius: "var(--ys-r-sm)",
    cursor: "pointer",
    flexShrink: 0,
    padding: 0,
  };

  const badgeEl = badge ? (
    <span
      aria-hidden="true"
      style={{
        position: "absolute",
        top: 4,
        insetInlineEnd: 4,
        minWidth: 16,
        height: 16,
        padding: "0 4px",
        borderRadius: "var(--ys-r-pill)",
        background: "var(--ys-primary)",
        color: "#fff",
        fontFamily: "var(--ys-font)",
        fontSize: 12,
        fontWeight: 600,
        lineHeight: "16px",
        textAlign: "center",
      }}
    >
      {badge}
    </span>
  ) : null;

  if (placement === "mobile") {
    return (
      <Link to="/notifications" aria-label={ariaLabel} style={hitArea}>
        <Bell size={20} aria-hidden="true" />
        {badgeEl}
      </Link>
    );
  }

  const items = listQuery.data ?? [];
  const anyUnread = items.some((n) => !n.read);

  // One card at a time: WhatsApp (D-16) first, else the first-open push ask (D-17).
  const slot =
    topSlot ??
    (whatsappVisible ? (
      <WhatsappOptInCard surface="bell_dropdown" />
    ) : showPushAsk && !whatsappShown.current ? (
      <PushSoftAsk surface="bell_first_open" />
    ) : null);

  return (
    <Popover open={open} onOpenChange={(next) => void handleOpenChange(next)}>
      <PopoverTrigger asChild>
        <button type="button" aria-label={ariaLabel} aria-haspopup="dialog" aria-expanded={open} style={hitArea}>
          <Bell size={20} aria-hidden="true" />
          {badgeEl}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        sideOffset={8}
        dir="rtl"
        className="p-0"
        style={{
          width: 384,
          maxHeight: 480,
          overflowY: "auto",
          background: "var(--ys-paper)",
          border: "1px solid var(--ys-rule)",
          borderRadius: "var(--ys-r-md)",
          fontFamily: "var(--ys-font)",
        }}
      >
        {slot && (
          // empty:hidden collapses the padding when the card decides not to render.
          <div className="empty:hidden" style={{ padding: "12px 12px 0" }}>
            {slot}
          </div>
        )}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            padding: 16,
            borderBottom: "1px solid var(--ys-rule)",
          }}
        >
          <span style={{ fontSize: 16, fontWeight: 600, lineHeight: 1.4, color: "var(--ys-ink)" }}>
            {NOTIFICATION_CHROME.title}
          </span>
          {anyUnread && (
            <button
              type="button"
              onClick={() => void handleMarkAll()}
              style={{
                fontFamily: "var(--ys-font)",
                fontSize: 14,
                color: "var(--ys-primary)",
                background: "transparent",
                border: "none",
                padding: 0,
                cursor: "pointer",
              }}
            >
              {NOTIFICATION_CHROME.markAllRead}
            </button>
          )}
        </div>
        <NotificationList
          surface="bell_dropdown"
          items={items}
          loading={listQuery.isLoading}
          error={listQuery.isError}
          onRetry={() => void listQuery.refetch()}
          limitSkeletons={3}
          onNavigate={() => setOpen(false)}
        />
        <div style={{ padding: 16, textAlign: "center" }}>
          <Link
            to="/notifications"
            onClick={() => setOpen(false)}
            style={{ fontSize: 14, fontWeight: 600, color: "var(--ys-primary)", textDecoration: "none" }}
          >
            {NOTIFICATION_CHROME.seeAll}
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}
